import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  IconButton,
  Divider,
  Link,
  Button,
} from "@mui/material";
import {
  Phone,
  Email,
  LocationOn,
  Facebook,
  Instagram,
  LinkedIn,
  Twitter,
  WhatsApp,
  ArrowForward,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const FooterContainer = styled(Box)`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #d32f2f, #ff6b6b, #d32f2f);
    background-size: 200% 100%;
    animation: gradient 3s ease infinite;
  }

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

const FooterLink = styled(Link)<{
  component?: React.ElementType;
  to?: string;
}>`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #ff6b6b;
    transform: translateX(4px);
  }
`;

const SocialButton = styled(IconButton)<{
  href?: string;
  target?: string;
  rel?: string;
  component?: React.ElementType;
}>`
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: #d32f2f;
    color: white;
    transform: translateY(-4px);
    border-color: #d32f2f;
  }
`;

const NewsletterInput = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  flex: 1;
  font-size: 1rem;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #ff6b6b;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const quickLinks = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.products"), path: "/products" },
    { label: t("nav.certificates"), path: "/certificates" },
    { label: t("nav.about"), path: "/about" },
    { label: t("nav.contact"), path: "/contact" },
  ];

  const products = [
    { label: "EI30", path: "/products" },
    { label: "EI60", path: "/products" },
    { label: "EI90", path: "/products" },
    { label: "EI120", path: "/products" },
    { label: t("products.filter.withGlass"), path: "/products" },
  ];

  const services = t("footer.services", { returnObjects: true }) as string[];

  return (
    <FooterContainer>
      <Container maxWidth="lg" sx={{ pt: 8, pb: 4 }}>
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src="/brand/yanmazkapi-logo.png"
              alt="Yanmaz Kapı"
              sx={{
                height: 72,
                width: "auto",
                display: "block",
                mb: 2,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 900,
                mb: 2,
                background: "linear-gradient(135deg, #ff6b6b, #d32f2f)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              🔥 {t("footer.company").toUpperCase()}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", mb: 3, lineHeight: 1.8 }}
            >
              {t("footer.description")}
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <LocationOn sx={{ color: "#ff6b6b", fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  {t("contact.info.addressLine1")}
                  <br />
                  {t("contact.info.addressLine2")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Phone sx={{ color: "#ff6b6b", fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  +90 552 837 83 77
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Email sx={{ color: "#ff6b6b", fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  info@yanmazkapi.com
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} md={2}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, mb: 2, color: "white" }}
            >
              {t("footer.quickLinksTitle")}
            </Typography>
            <Stack spacing={1.5}>
              {quickLinks.map((link) => (
                <FooterLink
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                >
                  <ArrowForward sx={{ fontSize: 14 }} />
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Products */}
          <Grid item xs={6} md={3}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, mb: 2, color: "white" }}
            >
              {t("footer.productsTitle")}
            </Typography>
            <Stack spacing={1.5}>
              {products.map((product) => (
                <FooterLink
                  key={product.label}
                  component={RouterLink}
                  to={product.path}
                >
                  <ArrowForward sx={{ fontSize: 14 }} />
                  {product.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Services & Newsletter */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, mb: 2, color: "white" }}
            >
              {t("footer.servicesTitle")}
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 4 }}>
              {services.map((service) => (
                <Typography
                  key={service}
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  ✓ {service}
                </Typography>
              ))}
            </Stack>

            <Typography
              variant="h6"
              sx={{ fontWeight: 700, mb: 2, color: "white" }}
            >
              {t("footer.newsletterTitle")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", mb: 2 }}
            >
              {t("footer.newsletterDesc")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <NewsletterInput
                type="email"
                placeholder={t("footer.newsletterPlaceholder")}
              />
              <Button
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #d32f2f, #ff6b6b)",
                  minWidth: "auto",
                  px: 2,
                }}
              >
                <ArrowForward />
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.1)" }} />

        {/* Bottom Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.5)",
              textAlign: { xs: "center", md: "left" },
            }}
          >
            {t("footer.copyright")}
          </Typography>

          <Stack direction="row" spacing={1}>
            <SocialButton
              component="a"
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Facebook />
            </SocialButton>
            <SocialButton
              component="a"
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram />
            </SocialButton>
            <SocialButton
              component="a"
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedIn />
            </SocialButton>
            <SocialButton
              component="a"
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Twitter />
            </SocialButton>
            <SocialButton
              component="a"
              href="https://wa.me/905528378377"
              target="_blank"
              rel="noopener noreferrer"
            >
              <WhatsApp />
            </SocialButton>
          </Stack>
        </Box>

        {/* Certificates Badge */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ flexWrap: "wrap", gap: 2 }}
          >
            {(t("footer.badges", { returnObjects: true }) as string[]).map(
              (cert) => (
                <Box
                  key={cert}
                  sx={{
                    px: 2,
                    py: 1,
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 2,
                    background: "rgba(255,255,255,0.02)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 600 }}
                  >
                    {cert}
                  </Typography>
                </Box>
              )
            )}
          </Stack>
        </Box>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
